import {UnionFinancialSettlement} from '../scheduling/UnionFinancialSettlement'
import { locationDisplayAddress } from '../../../services/locationAddress'
import React, { useEffect, useMemo, useState } from 'react'
import { ShiftHistoryViewer } from './ShiftHistoryViewer'
import {
  addTimesheetApprovalComment,
  adjustTimesheet,
  approveCompanyTimesheet,
  approveTimesheet,
  approveOfflinePunch,
  downloadTimesheetExport,
  listJobAssignments,
  listJobs,
  listPendingOfflinePunches,
  listReportingExportHistoryPage,
  listTimesheetApprovalHistory,
  listTimesheetApprovalsPage,
  listWorkerProfiles,
  previewTimesheetExportCsv,
  rejectTimesheet,
  rejectOfflinePunch,
  resolveTimesheetDispute,
} from '../../../services/access'
import type {
  AdminTimesheetAdjustPayload,
  AdminTimesheetResolvePayload,
  AccessibleLocation,
  Job,
  JobAssignment,
  OfflinePunchReview,
  Organization,
  ReportingExportHistoryRow,
  TimesheetApprovalHistoryRow,
  TimesheetApprovalQueueRow,
  TimesheetExportFilters,
  WorkerProfileDetail,
} from '../../../types/access'
import { useI18n } from '../../../i18n/I18nContext'
import { useAccessContext } from '../../../context/AccessContext'
import { formatOrgType } from './adminPanelUtils'
import { ConfirmDialog, EmptyState, ErrorBanner, FormModal, LoadingState, PaginationControls, SortableHeader } from './AdminUiPrimitives'
import { usePaginatedList } from './usePaginatedList'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'
import { GuidedSidePanel, type GuidedWorkflowDefinition } from './GuidedWorkflow'
import { useAdminExperience } from '../../../context/AdminExperienceContext'

interface TimesheetPayrollExportPanelProps {
  mode: 'approvals' | 'settlement'
  organizations: Organization[]
  accessibleLocations: AccessibleLocation[]
  canExportTimesheets: boolean
}

type TimesheetListFilters = Record<string, string | boolean | undefined>
type TimesheetExportFormat = NonNullable<TimesheetExportFilters['format']>

const pad = (value: number) => String(value).padStart(2, '0')

const dateInputValue = (date = new Date()) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const defaultStartDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 6)
  return dateInputValue(date)
}

const readable = (value?: string | null) => (value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || '-'
const dateTime = (value?: string | null) => value ? new Date(value).toLocaleString() : '-'
const toIsoDateTime = (value?: string | null) => {
  if (!value) return ''
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString()
}
const minutesLabel = (value?: string | number | null) => {
  const minutes = Number(value)
  if (!Number.isFinite(minutes)) return '-'
  const hours = minutes / 60
  return `${minutes} min (${hours.toFixed(2)} hr)`
}
const durationMinutes = (start?: string | null, end?: string | null, breakMinutes = 0) => {
  if (!start || !end) return 0
  return Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000) - breakMinutes)
}
const durationBetweenMinutes = (start?: string | null, end?: string | null) => durationMinutes(start, end, 0)
const hoursToMinutes = (value?: string | number | null) => {
  const hours = Number(value)
  return Number.isFinite(hours) ? Math.max(0, Math.round(hours * 60)) : null
}
const backendMinutes = (...values: Array<string | number | null | undefined>) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue
    const minutes = Number(value)
    if (Number.isFinite(minutes)) return minutes
  }
  return null
}

const approvalStateOf = (row: TimesheetApprovalQueueRow) => row.approval_state || 'completed'
const readinessStatusOf = (row: TimesheetApprovalQueueRow) => row.readiness_status || row.readinessStatus || 'pending_review'
const supervisorReviewStates = new Set(['completed', 'submitted', 'pending_review'])
const companyReviewStates = new Set(['approved_supervisor'])
const exportReadyStates = new Set(['approved', 'approved_company', 'resolved', 'approved_for_payroll', 'payroll_locked', 'exported'])
const blockedExportStates = new Set(['rejected', 'disputed', 'disputed_by_worker', 'adjusted_by_admin'])
const approvedSettlementStates = new Set(['approved', 'approved_company', 'resolved', 'approved_for_payroll', 'payroll_locked'])

const rowExportStatus = (row: TimesheetApprovalQueueRow) => {
  const state = approvalStateOf(row)
  const readiness = readinessStatusOf(row)
  const exportStatus = row.export_status || row.exportStatus
  if (state === 'exported' || exportStatus === 'exported' || readiness === 'already_exported') return 'Exported'
  if (readiness === 'ready_for_export') return 'Export ready'
  if (exportReadyStates.has(state)) return row.payroll_locked ? 'Export ready' : 'Payroll lock pending'
  if (blockedExportStates.has(state) || readiness === 'blocked') return 'Not exportable'
  return 'Not exportable'
}

const isActionBlocked = (row: TimesheetApprovalQueueRow) => ['payroll_locked', 'exported'].includes(approvalStateOf(row))
const canApproveRow = (row: TimesheetApprovalQueueRow) => !isActionBlocked(row) && !['rejected', 'disputed', 'disputed_by_worker', 'adjusted_by_admin'].includes(approvalStateOf(row))
const canAdjustRow = (row: TimesheetApprovalQueueRow) => !isActionBlocked(row) && !['rejected', 'disputed', 'disputed_by_worker'].includes(approvalStateOf(row))
const canRejectRow = (row: TimesheetApprovalQueueRow) => !isActionBlocked(row) && approvalStateOf(row) !== 'rejected'
const canResolveRow = (row: TimesheetApprovalQueueRow) => ['disputed', 'disputed_by_worker'].includes(approvalStateOf(row))
const scheduledMinutes = (row: TimesheetApprovalQueueRow) => backendMinutes(row.scheduled_minutes, row.scheduledMinutes) ?? durationBetweenMinutes(row.scheduled_start, row.scheduled_end)
const grossMinutes = (row: TimesheetApprovalQueueRow) => backendMinutes(row.gross_minutes, row.grossMinutes) ?? durationBetweenMinutes(row.start_time, row.end_time)
const workedMinutes = (row: TimesheetApprovalQueueRow) => backendMinutes(row.worked_minutes, row.workedMinutes) ?? durationMinutes(row.start_time, row.end_time, Number(row.break_minutes || 0))
const payableMinutes = (row: TimesheetApprovalQueueRow) => backendMinutes(row.payable_minutes, row.payableMinutes) ?? hoursToMinutes(row.final_total_hours) ?? hoursToMinutes(row.admin_adjusted_total_hours) ?? workedMinutes(row)
const payableSourceLabel = (row: TimesheetApprovalQueueRow) => {
  const source = row.payable_source || row.payableSource
  if (source === 'final_approved_total_hours') return 'Final approved total'
  if (source === 'admin_adjusted_total_hours') return 'Admin-adjusted total'
  if (source === 'worked_evidence_duration') return 'Worked evidence duration'
  return source ? readable(source) : 'Backend payable duration'
}
const reviewStatusLabel = (row: TimesheetApprovalQueueRow) => {
  const state = approvalStateOf(row)
  if (supervisorReviewStates.has(state)) return 'Awaiting supervisor review'
  if (companyReviewStates.has(state)) return 'Awaiting company approval'
  if (state === 'adjusted_by_admin') return 'Corrected, worker review'
  if (state === 'disputed_by_worker') return 'Worker dispute'
  if (state === 'disputed') return 'Disputed'
  if (state === 'rejected') return 'Rejected'
  if (approvedSettlementStates.has(state)) return 'Approved'
  if (state === 'exported') return 'Exported'
  return readable(state)
}
const settlementStatusLabel = (row: TimesheetApprovalQueueRow) => {
  const state = approvalStateOf(row)
  const readiness = readinessStatusOf(row)
  const exportStatus = row.export_status || row.exportStatus
  if (state === 'exported' || exportStatus === 'exported' || readiness === 'already_exported') return 'Exported'
  if (readiness === 'ready_for_export') return 'Ready for export'
  if (readiness === 'blocked') return 'Blocked from export'
  if (approvedSettlementStates.has(state) && row.payroll_locked) return 'Ready for export'
  if (approvedSettlementStates.has(state)) return 'Approved, lock pending'
  if (blockedExportStates.has(state)) return 'Blocked from export'
  return 'Pending review'
}
const exceptionIssues = (row: TimesheetApprovalQueueRow) => {
  const backendIssues = row.exception_codes || row.exceptionCodes
  if (backendIssues?.length) {
    return backendIssues.map((issue) => ({
      label: issue.label,
      severity: issue.blocking || issue.severity === 'blocking' ? 'blocking' as const : 'warning' as const,
      action: issue.settlementImpact || issue.source || 'Review backend exception evidence',
    }))
  }
  const issues: Array<{ label: string; severity: 'blocking' | 'warning'; action: string }> = []
  const breakMinutes = Number(row.break_minutes || 0)
  const gross = durationBetweenMinutes(row.start_time, row.end_time)
  const scheduled = scheduledMinutes(row)
  const worked = workedMinutes(row)
  if (!row.start_time) issues.push({ label: 'Missing punch-in', severity: 'blocking', action: 'Correct attendance evidence' })
  if (!row.end_time) issues.push({ label: 'Missing punch-out', severity: 'blocking', action: 'Correct attendance evidence' })
  if (row.first_break_start && !row.last_break_end) issues.push({ label: 'Missing break end', severity: 'blocking', action: 'Review break evidence' })
  if (gross <= 0 && row.start_time && row.end_time) issues.push({ label: 'Negative or zero duration', severity: 'blocking', action: 'Correct time interval' })
  if (breakMinutes > gross && gross > 0) issues.push({ label: 'Break exceeds worked interval', severity: 'blocking', action: 'Correct break duration' })
  if (scheduled > 0 && worked > scheduled + 120) issues.push({ label: 'Worked far outside scheduled duration', severity: 'warning', action: 'Confirm payable time' })
  if (row.admin_adjustment_reason || approvalStateOf(row) === 'adjusted_by_admin') issues.push({ label: 'Manual correction pending', severity: approvalStateOf(row) === 'adjusted_by_admin' ? 'blocking' : 'warning', action: 'Review correction history' })
  if (row.worker_dispute_message && !row.dispute_resolution_note) issues.push({ label: 'Unresolved worker dispute', severity: 'blocking', action: 'Resolve dispute' })
  if (row.reason && approvalStateOf(row) === 'rejected') issues.push({ label: 'Rejected with reason', severity: 'blocking', action: 'Keep visible in history' })
  return issues
}
const primaryExceptionLabel = (row: TimesheetApprovalQueueRow) => {
  const issues = exceptionIssues(row)
  if (!issues.length) return 'No blocking exception'
  return issues[0].label
}
const hasBlockingException = (row: TimesheetApprovalQueueRow) => exceptionIssues(row).some((issue) => issue.severity === 'blocking')
const canSettleRow = (row: TimesheetApprovalQueueRow) => readinessStatusOf(row) === 'ready_for_export' || (approvedSettlementStates.has(approvalStateOf(row)) && !hasBlockingException(row) && rowExportStatus(row) !== 'Exported')
const settlementBlockers = (row: TimesheetApprovalQueueRow) => {
  const backendBlockers = row.blocking_reasons || row.blockingReasons
  if (backendBlockers?.length) return backendBlockers
  const blockers = exceptionIssues(row).filter((issue) => issue.severity === 'blocking').map((issue) => issue.label)
  const state = approvalStateOf(row)
  if (!approvedSettlementStates.has(state) && state !== 'exported') blockers.push(`${reviewStatusLabel(row)} is not approved payable time`)
  if (state === 'exported') blockers.push('Already exported')
  if (!row.currency_code) blockers.push('Missing currency')
  return Array.from(new Set(blockers))
}

type PreviewRow = Record<string, string>

const parseCsvRows = (csvText: string): PreviewRow[] => {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let index = 0; index < csvText.length; index += 1) {
    const char = csvText[index]
    const next = csvText[index + 1]
    if (quoted && char === '"' && next === '"') {
      cell += '"'
      index += 1
    } else if (char === '"') {
      quoted = !quoted
    } else if (char === ',' && !quoted) {
      row.push(cell)
      cell = ''
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') index += 1
      row.push(cell)
      if (row.some((value) => value !== '')) rows.push(row)
      row = []
      cell = ''
    } else {
      cell += char
    }
  }
  row.push(cell)
  if (row.some((value) => value !== '')) rows.push(row)
  const [headers = [], ...body] = rows
  return body.map((values) => Object.fromEntries(headers.map((header, index) => [header, values[index] || ''])))
}

const rateLabel = (assignment?: JobAssignment, job?: Job) => {
  const rawRate = assignment?.resolved_pay_rate ?? assignment?.pay_rate ?? job?.pay_rate
  if (rawRate === null || rawRate === undefined || rawRate === '') return '-'
  const rate = Number(rawRate)
  if (!Number.isFinite(rate)) return '-'
  const currency = String(assignment?.resolved_currency_code || assignment?.currency_code || assignment?.rate_currency || job?.currency_code || '').toUpperCase()
  return `${rate.toFixed(2)}${currency ? ` ${currency}` : ''}/hr`
}

const locationLabel = (location: AccessibleLocation) => {
  const address = locationDisplayAddress(location)
  const label = location.name || location.label || location.location_key || 'Unnamed location'
  return address ? `${label} - ${address}` : label
}

const workerName = (worker: WorkerProfileDetail) => (
  worker.workerName ||
  worker.display_name ||
  [worker.first_name, worker.last_name].filter(Boolean).join(' ').trim() ||
  worker.workerEmail ||
  worker.worker_email ||
  'Worker profile'
)

const workerLabel = (worker: WorkerProfileDetail) => {
  const email = worker.workerEmail || worker.worker_email
  return email ? `${workerName(worker)} - ${email}` : workerName(worker)
}

const assignmentLabel = (assignment: JobAssignment, workerOptions: WorkerProfileDetail[]) => {
  const worker = assignment.worker_display_name ||
    assignment.worker_email ||
    workerOptions.find((option) => option.worker_profile_id === assignment.worker_profile_id)?.workerName ||
    'Worker'
  const shift = assignment.job_title || 'Job assignment'
  return `${shift} - ${worker} - ${readable(assignment.status)}`
}

const buildSummary = (
  filters: TimesheetExportFilters,
  options: {
    organizations: Organization[]
    locations: AccessibleLocation[]
    jobs: Job[]
    workers: WorkerProfileDetail[]
    assignments: JobAssignment[]
  },
) => {
  const chips = [
    filters.startDate || filters.endDate ? `Dates: ${filters.startDate || 'Beginning'} to ${filters.endDate || 'Latest'}` : 'Dates: all available',
    filters.locationId ? `Location: ${locationLabel(options.locations.find((location) => location.id === filters.locationId)! || {} as AccessibleLocation)}` : '',
    filters.workerId ? `Worker: ${workerLabel(options.workers.find((worker) => worker.worker_user_id === filters.workerId)! || {} as WorkerProfileDetail)}` : '',
    filters.agencyOrgId ? `Agency: ${options.organizations.find((org) => org.id === filters.agencyOrgId)?.name || 'Selected agency'}` : '',
    filters.companyOrgId ? `Company: ${options.organizations.find((org) => org.id === filters.companyOrgId)?.name || 'Selected company'}` : '',
    filters.jobId ? `Job: ${options.jobs.find((job) => job.id === filters.jobId)?.title || 'Selected job'}` : '',
    filters.jobAssignmentId ? `Assignment: ${assignmentLabel(options.assignments.find((assignment) => assignment.id === filters.jobAssignmentId)! || {} as JobAssignment, options.workers)}` : '',
    filters.approvalState ? `Approval: ${readable(filters.approvalState)}` : '',
    filters.exportState ? `Export state: ${readable(filters.exportState)}` : '',
    `Format: ${(filters.format || 'csv').toUpperCase()}`,
    filters.profileLabel ? `Template: ${filters.profileLabel}` : '',
  ].filter(Boolean)
  return chips
}

export const TimesheetPayrollExportPanel: React.FC<TimesheetPayrollExportPanelProps> = ({
  mode,
  organizations,
  accessibleLocations,
  canExportTimesheets,
}) => {
  const { t, formatNumber } = useI18n()
  const { isAdvanced } = useAdminExperience()
  const { accessContext, currentOrganization, currentBrandName, currentLogoUrl, brandStyleVars, hasAnyPermission } = useAccessContext()
  const industryLabels = useIndustryTerminology()
  const translatedStatus = (value?: string | null) => t(readable(value))
  const isSettlementMode = mode === 'settlement'
  const isApprovalsMode = mode === 'approvals'
  const canUseCustomReporting = accessContext?.billingEntitlements?.features?.['reporting.custom']?.enabled === true
  const customReportingContactSalesHref = `/demo-request?${new URLSearchParams({
    source: 'billing_feature_gate',
    campaign: 'reporting.custom',
    featureKey: 'reporting.custom',
    featureName: 'Custom Reporting',
    ...(accessContext?.billingEntitlements?.planKey ? { currentPlan: accessContext.billingEntitlements.planKey } : {}),
    ...(currentOrganization?.id ? { organizationId: currentOrganization.id } : {}),
    returnPath: `${window.location.pathname}${window.location.search}`,
  }).toString()}`
  const [filters, setFilters] = useState<TimesheetExportFilters>({
    startDate: defaultStartDate(),
    endDate: dateInputValue(),
    approvalState: mode === 'approvals' ? undefined : 'approved',
    exportState: mode === 'settlement' ? 'not_exported' : undefined,
  })
  const [jobs, setJobs] = useState<Job[]>([])
  const [workers, setWorkers] = useState<WorkerProfileDetail[]>([])
  const [assignments, setAssignments] = useState<JobAssignment[]>([])
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([])
  const [previewLoaded, setPreviewLoaded] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [offlinePunches, setOfflinePunches] = useState<OfflinePunchReview[]>([])
  const [offlineLoading, setOfflineLoading] = useState(false)
  const [offlineError, setOfflineError] = useState('')
  const [selectedPunch, setSelectedPunch] = useState<OfflinePunchReview | null>(null)
  const [approveTarget, setApproveTarget] = useState<OfflinePunchReview | null>(null)
  const [rejectTarget, setRejectTarget] = useState<OfflinePunchReview | null>(null)
  const [reviewNotes, setReviewNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [reviewBusy, setReviewBusy] = useState(false)
  const [approvalError, setApprovalError] = useState('')
  const [selectedTimesheet, setSelectedTimesheet] = useState<TimesheetApprovalQueueRow | null>(null)
  const [historyTimesheet, setHistoryTimesheet] = useState<TimesheetApprovalQueueRow | null>(null)
  const [approvalHistory, setApprovalHistory] = useState<TimesheetApprovalHistoryRow[]>([])
  const [approvalNote, setApprovalNote] = useState('')
  const [approveTargetTimesheet, setApproveTargetTimesheet] = useState<TimesheetApprovalQueueRow | null>(null)
  const [rejectTargetTimesheet, setRejectTargetTimesheet] = useState<TimesheetApprovalQueueRow | null>(null)
  const [adjustTargetTimesheet, setAdjustTargetTimesheet] = useState<TimesheetApprovalQueueRow | null>(null)
  const [resolveTargetTimesheet, setResolveTargetTimesheet] = useState<TimesheetApprovalQueueRow | null>(null)
  const [adjustForm, setAdjustForm] = useState<AdminTimesheetAdjustPayload>({ adjustedStartTime: '', adjustedEndTime: '', adjustedBreakMinutes: 0, adjustedTotalHours: 0, reason: '' })
  const [resolveForm, setResolveForm] = useState<AdminTimesheetResolvePayload>({ resolution: 'keep_admin_version', note: '' })
  const canViewPayRates = accessContext?.rateVisibility?.canViewPayRate !== false
  const canMarkExported = Boolean(accessContext?.rateVisibility?.canExportPayroll ?? canExportTimesheets)
  const canReviewTimesheets = hasAnyPermission(['tenant.timesheets.approve', 'tenant.timesheets.view', 'tenant.punches.review', 'tenant.rbac.manage'])
  const approvalList = usePaginatedList<TimesheetApprovalQueueRow, TimesheetListFilters>({
    defaultSortBy: 'shiftDate',
    defaultSortDir: 'desc',
    defaultFilters: filters as TimesheetListFilters,
    enabled: canExportTimesheets,
    fetchPage: (query) => canExportTimesheets
      ? listTimesheetApprovalsPage(query)
      : Promise.resolve({
        items: [],
        pagination: { page: query.page || 1, pageSize: query.pageSize || 25, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
        sort: { sortBy: query.sortBy, sortDir: query.sortDir || 'desc' },
        filters: query,
      }),
  })
  const approvalRows = approvalList.items
  const exportHistoryList = usePaginatedList<ReportingExportHistoryRow>({
    defaultSortBy: 'generated',
    defaultSortDir: 'desc',
    enabled: canExportTimesheets && isSettlementMode,
    fetchPage: (query) => canExportTimesheets && isSettlementMode
      ? listReportingExportHistoryPage(query)
      : Promise.resolve({
        items: [],
        pagination: { page: query.page || 1, pageSize: query.pageSize || 25, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
        sort: { sortBy: query.sortBy, sortDir: query.sortDir || 'desc' },
        filters: query,
      }),
  })
  const exportHistory = exportHistoryList.items

  const agencyOrgs = useMemo(() => organizations.filter((org) => org.org_type === 'agency'), [organizations])
  const companyOrgs = useMemo(() => organizations.filter((org) => org.org_type === 'company'), [organizations])
  const headerTitle = isSettlementMode
    ? t('Export workspace')
    : (industryLabels.explicitIndustry ? t('Approvals / {timesheet}', { timesheet: industryLabels.label('timesheet') }) : t('Approvals / Timesheets'))
  const headerDescription = isSettlementMode
    ? t('Turn approved hours into payroll, billing, and integration-ready export files.')
    : t('Review shift and punch evidence, resolve worker challenges, and approve payable hours before export.')
  const filterPanelTitle = isSettlementMode ? t('Export-ready filters') : t('Review queue filters')
  const filterPanelDescription = isSettlementMode
    ? t('Export defaults to approved, not-exported rows while keeping the same payroll filters and formats available.')
    : t('Approvals defaults to the broad review queue, including submitted rows needing supervisor or company review, worker challenges/disputes, comments, adjustments, and approval history close at hand.')
  const approvalQueueTitle = isSettlementMode ? t('Approved/export-ready {timesheets}', { timesheets: industryLabels.label('timesheet').toLowerCase() + 's' }) : t('{timesheet} approval queue', { timesheet: industryLabels.label('timesheet') })
  const approvalQueueDescription = isSettlementMode
    ? t('Approved company and payroll-locked shifts feed payroll exports. Review details before previewing, exporting, or marking rows exported.')
    : t('Review completed shift and punch evidence for pending supervisor approval, pending company approval, rejected/disputed rows, worker challenge context, comments, and payable-hour decisions.')
  const approvalStatusOptions = isSettlementMode
    ? [
      { value: '', label: 'All statuses' },
      { value: 'approved', label: 'Approved / export-ready' },
      { value: 'approved_company', label: 'Company approved' },
      { value: 'resolved', label: 'Resolved / export-ready' },
      { value: 'approved_for_payroll', label: 'Approved for payroll' },
      { value: 'payroll_locked', label: 'Payroll locked' },
      { value: 'exported', label: 'Exported' },
      { value: 'adjusted_by_admin', label: 'Adjusted' },
      { value: 'approved_supervisor', label: 'Supervisor approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'disputed', label: 'Disputed' },
      { value: 'disputed_by_worker', label: 'Worker disputed' },
    ]
    : [
      { value: '', label: 'All review states' },
      { value: 'completed', label: 'Completed / unsubmitted' },
      { value: 'submitted', label: 'Submitted / pending supervisor' },
      { value: 'pending_review', label: 'Pending review' },
      { value: 'approved_supervisor', label: 'Pending company approval' },
      { value: 'adjusted_by_admin', label: 'Adjusted / worker review' },
      { value: 'disputed', label: 'Disputed' },
      { value: 'disputed_by_worker', label: 'Worker disputed' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'approved', label: 'Approved / export-ready' },
      { value: 'approved_company', label: 'Company approved' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'approved_for_payroll', label: 'Approved for payroll' },
      { value: 'payroll_locked', label: 'Payroll locked' },
      { value: 'exported', label: 'Exported' },
    ]
  const approvalReviewStates = isSettlementMode
    ? ['approved', 'approved_company', 'resolved', 'approved_for_payroll', 'payroll_locked', 'exported', 'adjusted_by_admin', 'rejected', 'disputed', 'disputed_by_worker']
    : ['completed', 'submitted', 'pending_review', 'approved_supervisor', 'approved_company', 'adjusted_by_admin', 'disputed', 'disputed_by_worker', 'rejected', 'approved', 'resolved', 'approved_for_payroll', 'payroll_locked', 'exported']
  const workerOptions = useMemo(() => {
    const seen = new Set<string>()
    return workers.filter((worker) => {
      const workerUserId = worker.worker_user_id
      if (!workerUserId || seen.has(workerUserId)) return false
      seen.add(workerUserId)
      return true
    })
  }, [workers])
  const filteredAssignments = useMemo(() => assignments.filter((assignment) => {
    if (filters.jobId && assignment.job_id !== filters.jobId) return false
    if (filters.workerId) {
      const worker = workerOptions.find((option) => option.worker_profile_id === assignment.worker_profile_id)
      if (worker?.worker_user_id !== filters.workerId) return false
    }
    if (filters.agencyOrgId && assignment.supplying_agency_org_id !== filters.agencyOrgId && assignment.assigned_by_org_id !== filters.agencyOrgId) return false
    if (filters.companyOrgId && assignment.company_org_id !== filters.companyOrgId) return false
    return true
  }), [assignments, filters.agencyOrgId, filters.companyOrgId, filters.jobId, filters.workerId, workerOptions])

  const summaryChips = useMemo(() => buildSummary(filters, {
    organizations,
    locations: accessibleLocations,
    jobs,
    workers: workerOptions,
    assignments,
  }), [accessibleLocations, assignments, filters, jobs, organizations, workerOptions])
  const activeFilterCount = useMemo(() => {
    const defaultFilters = {
      startDate: defaultStartDate(),
      endDate: dateInputValue(),
      approvalState: mode === 'approvals' ? undefined : 'approved',
      exportState: mode === 'settlement' ? 'not_exported' : undefined,
      format: undefined,
    }
    return (Object.keys(filters) as Array<keyof TimesheetExportFilters>).filter((key) => {
      const value = filters[key]
      if (value === undefined || value === '' || value === false) return false
      return value !== defaultFilters[key as keyof typeof defaultFilters]
    }).length
  }, [filters, mode])
  const attentionRows = useMemo(() => (
    approvalRows
      .filter((row) => hasBlockingException(row) || supervisorReviewStates.has(approvalStateOf(row)) || companyReviewStates.has(approvalStateOf(row)))
      .slice(0, 6)
  ), [approvalRows])
  const approvalSummary = useMemo(() => ({
    awaitingReview: approvalRows.filter((row) => supervisorReviewStates.has(approvalStateOf(row)) || companyReviewStates.has(approvalStateOf(row))).length,
    blockingExceptions: approvalRows.filter(hasBlockingException).length,
    approved: approvalRows.filter((row) => approvedSettlementStates.has(approvalStateOf(row))).length,
    rejected: approvalRows.filter((row) => approvalStateOf(row) === 'rejected').length,
    corrected: approvalRows.filter((row) => row.admin_adjustment_reason || approvalStateOf(row) === 'adjusted_by_admin').length,
    settlementReady: approvalRows.filter(canSettleRow).length,
  }), [approvalRows])
  const settlementRows = useMemo(() => approvalRows.filter((row) => canSettleRow(row) || settlementBlockers(row).length > 0), [approvalRows])
  const settlementSummary = useMemo(() => {
    const readyRows = approvalRows.filter(canSettleRow)
    const blockedRows = approvalRows.filter((row) => settlementBlockers(row).length > 0 && !canSettleRow(row))
    return {
      ready: readyRows.length,
      blocked: blockedRows.length,
      alreadyExported: approvalRows.filter((row) => readinessStatusOf(row) === 'already_exported' || approvalStateOf(row) === 'exported' || row.export_status === 'exported' || row.exportStatus === 'exported').length,
      pendingReview: approvalRows.filter((row) => readinessStatusOf(row) === 'pending_review' || (!approvedSettlementStates.has(approvalStateOf(row)) && approvalStateOf(row) !== 'exported')).length,
      payableMinutes: readyRows.reduce((sum, row) => sum + payableMinutes(row), 0),
      currencies: Array.from(new Set(readyRows.map((row) => row.currency_code || row.currencyCode).filter(Boolean))).join(', ') || '-',
    }
  }, [approvalRows])
  const guidedWorkflow = useMemo<GuidedWorkflowDefinition>(() => isApprovalsMode ? ({
    id: 'worked-time-live',
    title: t('Review Worked Time'),
    description: t('The checklist follows the current backend approval queue. Guidance does not change approval or payroll eligibility.'),
    steps: [
      { id: 'period', title: t('Choose period'), status: filters.startDate && filters.endDate ? 'complete' : 'current' },
      { id: 'review', title: t('Review worked time'), status: approvalRows.length > 0 ? 'complete' : 'todo', description: t('{count} records are in the current result.', { count: approvalRows.length }) },
      { id: 'exceptions', title: t('Resolve exceptions'), status: approvalSummary.blockingExceptions > 0 ? 'current' : 'complete', description: t('{count} blocking exceptions or disputes remain.', { count: approvalSummary.blockingExceptions }) },
      { id: 'approve', title: t('Approve'), status: approvalSummary.blockingExceptions > 0 ? 'blocked' : approvalSummary.awaitingReview > 0 ? 'current' : 'complete', description: t('{count} records await approval.', { count: approvalSummary.awaitingReview }), blockingReason: approvalSummary.blockingExceptions > 0 ? t('Resolve blocking exceptions before approving payable time.') : undefined },
      { id: 'payroll', title: t('Prepare Payroll'), status: approvalSummary.blockingExceptions > 0 || approvalSummary.awaitingReview > 0 ? 'blocked' : approvalSummary.settlementReady > 0 ? 'current' : 'todo', description: t('{count} approved records are ready for payroll preparation.', { count: approvalSummary.settlementReady }), blockingReason: approvalSummary.blockingExceptions > 0 || approvalSummary.awaitingReview > 0 ? t('Finish exception resolution and approvals before preparing payroll.') : undefined },
    ],
  }) : ({
    id: 'prepare-payroll-live',
    title: t('Prepare Payroll'),
    description: t('The checklist follows canonical payroll-ready state for the selected period.'),
    steps: [
      { id: 'period', title: t('Choose period'), status: filters.startDate && filters.endDate ? 'complete' : 'current' },
      { id: 'exceptions', title: t('Review payroll blockers'), status: settlementSummary.blocked > 0 ? 'current' : 'complete', description: t('{count} rows require attention.', { count: settlementSummary.blocked }) },
      { id: 'payable', title: t('Review payable time'), status: settlementSummary.ready > 0 ? 'current' : settlementSummary.blocked > 0 ? 'blocked' : 'todo', description: t('{count} rows are ready for export.', { count: settlementSummary.ready }), blockingReason: settlementSummary.ready === 0 && settlementSummary.blocked > 0 ? t('Resolve the reported blockers before exporting.') : undefined },
      { id: 'export', title: t('Export Payroll'), status: settlementSummary.ready > 0 ? 'current' : 'todo', description: t('{hours} payable hours are ready.', { hours: (settlementSummary.payableMinutes / 60).toFixed(2) }) },
    ],
  }), [approvalRows, approvalSummary.awaitingReview, approvalSummary.blockingExceptions, approvalSummary.settlementReady, filters.endDate, filters.startDate, isApprovalsMode, settlementSummary.blocked, settlementSummary.payableMinutes, settlementSummary.ready, t])

  useEffect(() => {
    if (!canExportTimesheets) return
    let mounted = true
    setLoadingOptions(true)
    setError('')
    Promise.all([
      listJobs(),
      listWorkerProfiles(),
      listJobAssignments(),
    ])
      .then(([jobRows, workerRows, assignmentRows]) => {
        if (!mounted) return
        setJobs(jobRows)
        setWorkers(workerRows)
        setAssignments(assignmentRows)
      })
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load export filter options.')))
      .finally(() => mounted && setLoadingOptions(false))
    return () => { mounted = false }
  }, [canExportTimesheets])

  const loadOfflinePunches = async () => {
    if (!canExportTimesheets || !isApprovalsMode) return
    setOfflineLoading(true)
    setOfflineError('')
    try {
      const rows = await listPendingOfflinePunches({
        locationId: filters.locationId,
        workerProfileId: workerOptions.find((worker) => worker.worker_user_id === filters.workerId)?.worker_profile_id,
        jobAssignmentId: filters.jobAssignmentId,
        status: 'pending_review',
      })
      setOfflinePunches(rows)
    } catch (err: any) {
      setOfflineError(err?.response?.data?.message || 'Unable to load offline punches pending review.')
    } finally {
      setOfflineLoading(false)
    }
  }

  useEffect(() => {
    if (!isApprovalsMode) return
    loadOfflinePunches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canExportTimesheets, isApprovalsMode, filters.locationId, filters.workerId, filters.jobAssignmentId, workerOptions.length])

  const resetFilters = () => {
    setFilters({
      startDate: defaultStartDate(),
      endDate: dateInputValue(),
      approvalState: mode === 'approvals' ? undefined : 'approved',
      exportState: mode === 'settlement' ? 'not_exported' : undefined,
    })
    setPreviewRows([])
    setPreviewLoaded(false)
    setMessage('')
    setError('')
  }

  const buildExportFilters = (overrides: Partial<TimesheetExportFilters> = {}): TimesheetExportFilters => {
    const selectedOrgId = currentOrganization?.id
    const selectedOrgType = currentOrganization?.org_type
    return {
      ...filters,
      ...(selectedOrgId ? { organizationId: selectedOrgId } : {}),
      ...(selectedOrgId && selectedOrgType === 'company' && !filters.companyOrgId ? { companyOrgId: selectedOrgId } : {}),
      ...(selectedOrgId && selectedOrgType === 'agency' && !filters.agencyOrgId ? { agencyOrgId: selectedOrgId } : {}),
      ...overrides,
    }
  }

  const loadApprovals = async () => {
    if (!canExportTimesheets) return
    setApprovalError('')
    try {
      await approvalList.refresh()
    } catch (err: any) {
      setApprovalError(err?.response?.data?.message || t('Unable to load timesheet approvals.'))
    }
  }

  useEffect(() => {
    ;(['startDate', 'endDate', 'locationId', 'workerId', 'agencyOrgId', 'companyOrgId', 'jobId', 'jobAssignmentId', 'approvalState', 'exportState'] as const)
      .forEach((key) => approvalList.setFilter(key, filters[key] || undefined))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, filters.locationId, filters.workerId, filters.agencyOrgId, filters.companyOrgId, filters.jobId, filters.jobAssignmentId, filters.approvalState, filters.exportState])

  const openTimesheet = async (row: TimesheetApprovalQueueRow) => {
    setSelectedTimesheet(row)
    setApprovalNote('')
    try {
      setApprovalHistory(await listTimesheetApprovalHistory(row.shift_id))
    } catch {
      setApprovalHistory([])
    }
  }

  const openAdjustTimesheet = (row: TimesheetApprovalQueueRow) => {
    const breakMinutes = Number(row.admin_adjusted_break_minutes ?? row.final_break_minutes ?? row.break_minutes ?? 0)
    const totalHours = Number(row.admin_adjusted_total_hours ?? row.final_total_hours ?? (durationMinutes(row.start_time, row.end_time, breakMinutes) / 60).toFixed(2))
    setAdjustForm({
      adjustedStartTime: row.admin_adjusted_start_time || row.final_start_time || row.start_time || '',
      adjustedEndTime: row.admin_adjusted_end_time || row.final_end_time || row.end_time || '',
      adjustedBreakMinutes: Number.isFinite(breakMinutes) ? breakMinutes : 0,
      adjustedTotalHours: Number.isFinite(totalHours) ? totalHours : 0,
      reason: row.admin_adjustment_reason || '',
    })
    setAdjustTargetTimesheet(row)
  }

  const openResolveTimesheet = (row: TimesheetApprovalQueueRow) => {
    setResolveForm({
      resolution: row.admin_adjusted_start_time || row.admin_adjusted_end_time ? 'keep_admin_version' : 'accept_worker_version',
      note: row.dispute_resolution_note || '',
      finalStartTime: row.final_start_time || row.admin_adjusted_start_time || row.start_time || '',
      finalEndTime: row.final_end_time || row.admin_adjusted_end_time || row.end_time || '',
      finalBreakMinutes: Number(row.final_break_minutes ?? row.admin_adjusted_break_minutes ?? row.break_minutes ?? 0),
      finalTotalHours: Number(row.final_total_hours ?? row.admin_adjusted_total_hours ?? (durationMinutes(row.start_time, row.end_time, Number(row.break_minutes || 0)) / 60).toFixed(2)),
    })
    setResolveTargetTimesheet(row)
  }

  const handleApproveTimesheet = async (row: TimesheetApprovalQueueRow) => {
    if (reviewBusy) return
    setReviewBusy(true)
    setApprovalError('')
    try {
      if (row.approval_state === 'approved_supervisor') {
        await approveCompanyTimesheet(row.shift_id, approvalNote || undefined)
      } else {
        await approveTimesheet(row.shift_id, approvalNote || undefined)
      }
      setMessage(t('Timesheet approved. Approved payable hours are available to export with the approved status filter.'))
      setApproveTargetTimesheet(null)
      setSelectedTimesheet(null)
      await loadApprovals()
      setPreviewLoaded(false)
    } catch (err: any) {
      setApprovalError(err?.response?.data?.message || t('Unable to approve timesheet.'))
    } finally {
      setReviewBusy(false)
    }
  }

  const handleRejectTimesheet = async () => {
    if (!rejectTargetTimesheet || !approvalNote.trim() || reviewBusy) return
    setReviewBusy(true)
    setApprovalError('')
    try {
      await rejectTimesheet(rejectTargetTimesheet.shift_id, approvalNote.trim())
      setMessage(t('Timesheet rejected/disputed. It will stay out of approved payable exports.'))
      setRejectTargetTimesheet(null)
      setSelectedTimesheet(null)
      setApprovalNote('')
      await loadApprovals()
      setPreviewLoaded(false)
    } catch (err: any) {
      setApprovalError(err?.response?.data?.message || t('Unable to reject timesheet.'))
    } finally {
      setReviewBusy(false)
    }
  }

  const handleAdjustTimesheet = async () => {
    if (!adjustTargetTimesheet || reviewBusy) return
    if (!adjustForm.adjustedStartTime || !adjustForm.adjustedEndTime || !adjustForm.reason.trim()) {
      setApprovalError(t('Adjustment requires adjusted start, adjusted end, break minutes, total hours, and reason.'))
      return
    }
    setReviewBusy(true)
    setApprovalError('')
    try {
      await adjustTimesheet(adjustTargetTimesheet.shift_id, {
        ...adjustForm,
        adjustedStartTime: toIsoDateTime(adjustForm.adjustedStartTime),
        adjustedEndTime: toIsoDateTime(adjustForm.adjustedEndTime),
        adjustedBreakMinutes: Number(adjustForm.adjustedBreakMinutes || 0),
        adjustedTotalHours: Number(adjustForm.adjustedTotalHours || 0),
        reason: adjustForm.reason.trim(),
      })
      setMessage(t('Timesheet adjusted. It remains out of payroll export until the worker accepts it or a dispute is resolved.'))
      setAdjustTargetTimesheet(null)
      setSelectedTimesheet(null)
      await loadApprovals()
      setPreviewLoaded(false)
    } catch (err: any) {
      setApprovalError(err?.response?.data?.message || t('Unable to adjust timesheet.'))
    } finally {
      setReviewBusy(false)
    }
  }

  const handleResolveTimesheet = async () => {
    if (!resolveTargetTimesheet || reviewBusy) return
    if (!resolveForm.note?.trim()) {
      setApprovalError(t('Resolve dispute requires a resolution note.'))
      return
    }
    if (resolveForm.resolution === 'manual_final_values' && (!resolveForm.finalStartTime || !resolveForm.finalEndTime)) {
      setApprovalError(t('Manual dispute resolution requires final start and end times.'))
      return
    }
    setReviewBusy(true)
    setApprovalError('')
    try {
      await resolveTimesheetDispute(resolveTargetTimesheet.shift_id, {
        ...resolveForm,
        note: resolveForm.note.trim(),
        finalStartTime: resolveForm.resolution === 'manual_final_values' ? toIsoDateTime(resolveForm.finalStartTime) : undefined,
        finalEndTime: resolveForm.resolution === 'manual_final_values' ? toIsoDateTime(resolveForm.finalEndTime) : undefined,
        finalBreakMinutes: resolveForm.resolution === 'manual_final_values' ? Number(resolveForm.finalBreakMinutes || 0) : undefined,
        finalTotalHours: resolveForm.resolution === 'manual_final_values' ? Number(resolveForm.finalTotalHours || 0) : undefined,
      })
      setMessage(t('Timesheet dispute resolved. Resolved, payroll-locked final values are eligible for payroll export.'))
      setResolveTargetTimesheet(null)
      setSelectedTimesheet(null)
      await loadApprovals()
      setPreviewLoaded(false)
    } catch (err: any) {
      setApprovalError(err?.response?.data?.message || t('Unable to resolve timesheet dispute.'))
    } finally {
      setReviewBusy(false)
    }
  }

  const handleAddNote = async (row: TimesheetApprovalQueueRow) => {
    if (!approvalNote.trim() || reviewBusy) return
    setReviewBusy(true)
    setApprovalError('')
    try {
      await addTimesheetApprovalComment(row.shift_id, approvalNote.trim())
      setApprovalNote('')
      setApprovalHistory(await listTimesheetApprovalHistory(row.shift_id))
      setMessage(t('Reviewer note added to the timesheet audit trail.'))
    } catch (err: any) {
      setApprovalError(err?.response?.data?.message || t('Unable to add reviewer note.'))
    } finally {
      setReviewBusy(false)
    }
  }

  const updateFilter = (key: keyof TimesheetExportFilters, value: string) => {
    setFilters((current) => ({
      ...current,
      [key]: value || undefined,
      ...(key === 'jobId' || key === 'workerId' || key === 'agencyOrgId' || key === 'companyOrgId' ? { jobAssignmentId: undefined } : {}),
    }))
    setPreviewRows([])
    setPreviewLoaded(false)
  }

  const handlePreview = async () => {
    if (!canExportTimesheets || previewLoading) return
    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setError(t('Start date must be on or before end date.'))
      return
    }
    setPreviewLoading(true)
    setPreviewLoaded(false)
    setError('')
    setMessage('')
    try {
      const csvText = await previewTimesheetExportCsv(buildExportFilters({ format: 'csv' }))
      setPreviewRows(parseCsvRows(csvText))
      setPreviewLoaded(true)
    } catch (err: any) {
      const data = err?.response?.data
      let detail = ''
      if (data instanceof Blob) {
        detail = await data.text().catch(() => '')
      }
      setError(detail || err?.response?.data?.message || t('Unable to load the export preview.'))
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleDownload = async (format: TimesheetExportFormat = filters.format || 'csv') => {
    if (!canExportTimesheets || downloading) return
    if ((format === 'xlsx' || format === 'pdf') && !canUseCustomReporting) {
      setError(t('XLSX and PDF exports require the custom reporting add-on. Contact Sales to enable it.'))
      return
    }
    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setError(t('Start date must be on or before end date.'))
      return
    }
    setDownloading(true)
    setError('')
    setMessage('')
    try {
      const { blob, filename } = await downloadTimesheetExport(buildExportFilters({ format }))
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      setMessage(t('{format} download started: {filename}', { format: format.toUpperCase(), filename }))
      exportHistoryList.refresh()
    } catch (err: any) {
      const data = err?.response?.data
      let detail = ''
      if (data instanceof Blob) {
        detail = await data.text().catch(() => '')
      }
      setError(detail || err?.response?.data?.message || t('Unable to download the {format} export.', { format: format.toUpperCase() }))
    } finally {
      setDownloading(false)
    }
  }

  const loadExportHistory = async () => {
    if (!canExportTimesheets) return
    try {
      await exportHistoryList.refresh()
    } catch {
    } finally {
    }
  }

  const confirmApprove = async () => {
    if (!approveTarget) return
    setReviewBusy(true)
    setOfflineError('')
    try {
      await approveOfflinePunch(approveTarget.id, reviewNotes || undefined)
      setMessage(t('Offline punch approved. Approved offline punches are eligible for timesheet/payroll export.'))
      setApproveTarget(null)
      setReviewNotes('')
      await loadOfflinePunches()
    } catch (err: any) {
      setOfflineError(err?.response?.data?.message || t('Unable to approve offline punch.'))
    } finally {
      setReviewBusy(false)
    }
  }

  const confirmReject = async () => {
    if (!rejectTarget || !rejectionReason.trim()) return
    setReviewBusy(true)
    setOfflineError('')
    try {
      await rejectOfflinePunch(rejectTarget.id, rejectionReason.trim(), reviewNotes || undefined)
      setMessage(t('Offline punch rejected. Rejected offline punches remain available for audit and are excluded from payable time.'))
      setRejectTarget(null)
      setRejectionReason('')
      setReviewNotes('')
      await loadOfflinePunches()
    } catch (err: any) {
      setOfflineError(err?.response?.data?.message || t('Unable to reject offline punch.'))
    } finally {
      setReviewBusy(false)
    }
  }

  if (!canExportTimesheets) {
    return (
      <p className="admin-disabled-callout">
        {industryLabels.explicitIndustry ? t('{timesheet} and {payroll} CSV export requires payroll/export permission. The backend remains the source of truth and currently limits this broad export to tenant admin access.', { timesheet: industryLabels.label('timesheet'), payroll: industryLabels.label('payroll').toLowerCase() }) : t('Timesheet and payroll CSV export requires payroll/export permission. The backend remains the source of truth and currently limits this broad export to tenant admin access.')}
      </p>
    )
  }

  return (
    <div className="stack" data-testid="admin-timesheet-payroll-panel" style={brandStyleVars}>
      <UnionFinancialSettlement/>
      <GuidedSidePanel workflow={guidedWorkflow} />
      <div className="admin-panel-toolbar" style={{ borderTop: '3px solid var(--brand-primary, var(--primary))', paddingTop: '.75rem' }}>
        <div className="inline-group" style={{ alignItems: 'center' }}>
          {currentLogoUrl ? <img src={currentLogoUrl} alt="" className="admin-brand-logo admin-brand-logo--sm" /> : null}
          <div>
          <p className="helper" style={{ margin: 0 }}>{currentBrandName}</p>
          <h3 style={{ margin: 0 }}>{t(headerTitle)}</h3>
          <p className="helper">{t(headerDescription)}</p>
          </div>
        </div>
        <div className="inline-group">
          <span className="admin-chip">{t('Active filters')}: {formatNumber(activeFilterCount)}</span>
          <button type="button" className="btn btn-secondary" onClick={resetFilters} disabled={downloading || previewLoading || activeFilterCount === 0}>{t('Clear filters')}</button>
          {isApprovalsMode ? (
            <button type="button" className="btn btn-primary" onClick={loadApprovals} disabled={approvalList.loading}>{t('Refresh review queue')}</button>
          ) : (
            <>
              <button type="button" className="btn btn-primary" onClick={handlePreview} disabled={previewLoading || downloading || loadingOptions || Boolean(filters.startDate && filters.endDate && filters.startDate > filters.endDate)}>
                {previewLoading ? t('Loading preview...') : t('Preview rows')}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => handleDownload('csv')} disabled={downloading || loadingOptions || Boolean(filters.startDate && filters.endDate && filters.startDate > filters.endDate)}>
                {downloading ? t('Downloading...') : t('Export CSV')}
              </button>
              {isAdvanced && canUseCustomReporting ? (
                <button type="button" className="btn btn-secondary" onClick={() => handleDownload('xlsx')} disabled={downloading || loadingOptions || Boolean(filters.startDate && filters.endDate && filters.startDate > filters.endDate)}>
                  {downloading ? t('Downloading...') : t('Export XLSX')}
                </button>
              ) : isAdvanced ? <a className="btn btn-secondary" href={customReportingContactSalesHref}>🔒 {t('Contact Sales for XLSX')}</a> : null}
              {isAdvanced ? <button type="button" className="btn btn-secondary" onClick={() => handleDownload('json')} disabled={downloading || loadingOptions || Boolean(filters.startDate && filters.endDate && filters.startDate > filters.endDate)}>
                {downloading ? t('Downloading...') : t('Export JSON')}
              </button> : null}
              {isAdvanced && canUseCustomReporting ? (
                <button type="button" className="btn btn-primary" onClick={() => handleDownload('pdf')} disabled={downloading || loadingOptions || Boolean(filters.startDate && filters.endDate && filters.startDate > filters.endDate)}>
                  {downloading ? t('Downloading...') : t('Export PDF')}
                </button>
              ) : isAdvanced ? <a className="btn btn-primary" href={customReportingContactSalesHref}>🔒 {t('Contact Sales for PDF')}</a> : null}
            </>
          )}
        </div>
      </div>

      {message ? <p className="success-text">{message}</p> : null}
      {error ? <p className="error-text">{error}</p> : null}
      {loadingOptions ? <p className="text-muted">{t('Loading export filters...')}</p> : null}

      <section className="panel workspace-panel admin-edit-panel stack">
        <div>
          <h3 style={{ margin: 0 }}>{t(filterPanelTitle)}</h3>
          <p className="helper">{t(filterPanelDescription)}</p>
        </div>
        <div className="admin-form-grid">
          <label>{industryLabels.explicitIndustry ? t('{timesheet} start date', { timesheet: industryLabels.label('timesheet') }) : t('Timesheet start date')}<input type="date" className="input" value={filters.startDate || ''} onChange={(event) => updateFilter('startDate', event.target.value)} /></label>
          <label>{industryLabels.explicitIndustry ? t('{timesheet} end date', { timesheet: industryLabels.label('timesheet') }) : t('Timesheet end date')}<input type="date" className="input" value={filters.endDate || ''} onChange={(event) => updateFilter('endDate', event.target.value)} /></label>
          <label>{industryLabels.explicitIndustry ? t('Work {location}', { location: industryLabels.label('location').toLowerCase() }) : t('Work location')}<select className="input" value={filters.locationId || ''} onChange={(event) => updateFilter('locationId', event.target.value)}><option value="">{industryLabels.explicitIndustry ? t('All {resources}', { resources: industryLabels.label('locations').toLowerCase() }) : t('All locations')}</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}</select></label>
          {isAdvanced ? <>
          <label>{industryLabels.explicitIndustry ? t('{worker} profile', { worker: industryLabels.label('worker') }) : t('Worker profile')}<select className="input" value={filters.workerId || ''} onChange={(event) => updateFilter('workerId', event.target.value)}><option value="">{industryLabels.explicitIndustry ? t('All {resources}', { resources: industryLabels.label('workers').toLowerCase() }) : t('All workers')}</option>{workerOptions.map((worker) => <option key={worker.worker_user_id} value={worker.worker_user_id}>{workerLabel(worker)}</option>)}</select></label>
          <label>{t('Agency')}<select className="input" value={filters.agencyOrgId || ''} onChange={(event) => updateFilter('agencyOrgId', event.target.value)}><option value="">{t('All agencies')}</option>{agencyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name} ({formatOrgType(org.org_type)})</option>)}</select></label>
          <label>{t('Company')}<select className="input" value={filters.companyOrgId || ''} onChange={(event) => updateFilter('companyOrgId', event.target.value)}><option value="">{t('All companies')}</option>{companyOrgs.map((org) => <option key={org.id} value={org.id}>{org.name} ({formatOrgType(org.org_type)})</option>)}</select></label>
          <label>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job / order')}<select className="input" value={filters.jobId || ''} onChange={(event) => updateFilter('jobId', event.target.value)}><option value="">{industryLabels.explicitIndustry ? t('All {resources}', { resources: industryLabels.label('jobs').toLowerCase() }) : t('All jobs / orders')}</option>{jobs.map((job) => <option key={job.id} value={job.id}>{job.title}{job.company_org_name ? ` - ${job.company_org_name}` : ''}</option>)}</select></label>
          <label>{industryLabels.explicitIndustry ? t('{worker} {assignment}', { worker: industryLabels.label('worker'), assignment: industryLabels.label('assignment').toLowerCase() }) : t('Worker assignment')}<select className="input" value={filters.jobAssignmentId || ''} onChange={(event) => updateFilter('jobAssignmentId', event.target.value)}><option value="">{industryLabels.explicitIndustry ? t('All {resources}', { resources: industryLabels.label('assignments').toLowerCase() }) : t('All assignments')}</option>{filteredAssignments.map((assignment) => <option key={assignment.id} value={assignment.id}>{assignmentLabel(assignment, workerOptions)}</option>)}</select></label>
          </> : null}
          <label>{t('Approval status')}<select className="input" value={filters.approvalState || ''} onChange={(event) => updateFilter('approvalState', event.target.value)}>{approvalStatusOptions.map((option) => <option key={option.value || 'all'} value={option.value}>{t(option.label)}</option>)}</select></label>
          {isSettlementMode ? (
            <>
              <label>{t('Export state')}<select className="input" value={filters.exportState || ''} onChange={(event) => updateFilter('exportState', event.target.value)}><option value="">{t('All export states')}</option><option value="not_exported">{t('Not exported')}</option><option value="exported">{t('Exported')}</option></select></label>
              {isAdvanced ? <label>{t('Format')}<select className="input" value={filters.format || 'csv'} onChange={(event) => updateFilter('format', event.target.value)}><option value="csv">CSV</option><option value="json">{t('JSON integration payload')}</option><option value="xlsx" disabled={!canUseCustomReporting}>{canUseCustomReporting ? t('XLSX workbook') : `🔒 ${t('XLSX workbook - Contact Sales')}`}</option><option value="pdf" disabled={!canUseCustomReporting}>{canUseCustomReporting ? t('PDF report') : `🔒 ${t('PDF report - Contact Sales')}`}</option></select></label> : null}
              {isAdvanced ? <label>{t('Template label')}<input className="input" value={filters.profileLabel || ''} onChange={(event) => updateFilter('profileLabel', event.target.value)} placeholder={t('Punch standard payroll')} /></label> : null}
              <label><input type="checkbox" checked={Boolean(filters.markExported && canMarkExported)} onChange={(event) => setFilters((current) => ({ ...current, markExported: event.target.checked && canMarkExported ? true : undefined }))} disabled={!canMarkExported || ['rejected', 'disputed', 'disputed_by_worker', 'adjusted_by_admin'].includes(filters.approvalState || '')} /> {t('Mark approved rows exported after download')}</label>
            </>
          ) : null}
        </div>
        {isSettlementMode ? (
          <p className="helper">{industryLabels.explicitIndustry ? t('{payroll} export uses backend payroll-ready filtering by default. Unresolved disputes, rejected rows, and admin-adjusted rows awaiting {worker} acceptance are excluded and cannot be marked exported from this UI.', { payroll: industryLabels.label('payroll'), worker: industryLabels.label('worker').toLowerCase() }) : t('Payroll export uses backend payroll-ready filtering by default. Unresolved disputes, rejected rows, and admin-adjusted rows awaiting worker acceptance are excluded and cannot be marked exported from this UI.')}</p>
        ) : (
          <p className="helper">{industryLabels.explicitIndustry ? t('Exports are managed in {payroll} Export. This view is for shift and punch review, {worker} challenges/disputes, supervisor approval, company approval, comments, and adjustment history.', { payroll: industryLabels.label('payroll'), worker: industryLabels.label('worker').toLowerCase() }) : t('Exports are managed in Payroll Export. This view is for shift and punch review, worker challenges/disputes, supervisor approval, company approval, comments, and adjustment history.')}</p>
        )}
      </section>

      {isApprovalsMode ? (
        <section className="panel workspace-panel stack">
          <div>
            <h3 style={{ margin: 0 }}>{t('Timesheet review summary')}</h3>
            <p className="helper">{t('Attendance evidence is operational proof. Payable time is approved separately and only approved, payroll-locked rows can move toward export.')}</p>
          </div>
          <div className="admin-checklist-grid">
            <div className="admin-checklist-item"><span>{formatNumber(approvalSummary.awaitingReview)}</span><strong>{t('Awaiting review')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(approvalSummary.blockingExceptions)}</span><strong>{t('Blocking exceptions')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(approvalSummary.approved)}</span><strong>{t('Approved')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(approvalSummary.rejected)}</span><strong>{t('Rejected')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(approvalSummary.corrected)}</span><strong>{t('Corrected')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(approvalSummary.settlementReady)}</span><strong>{t('Ready for export')}</strong></div>
          </div>
          <p className="helper">{industryLabels.explicitIndustry ? t('{worker} disputes, admin adjustments, rejection reasons, final values, and resolution notes are shown on each row and in the detail panel when the backend returns them.', { worker: industryLabels.label('worker') }) : t('Worker disputes, admin adjustments, rejection reasons, final values, and resolution notes are shown on each row and in the detail panel when the backend returns them.')}</p>
        </section>
      ) : null}

      {isApprovalsMode ? (
        <section className="panel workspace-panel stack" aria-label={t('Timesheet attention queue')}>
          <div>
            <h3 style={{ margin: 0 }}>{t('Attention queue')}</h3>
            <p className="helper">{t('Records requiring an explicit review decision appear first. Exception resolution does not approve payable time by itself.')}</p>
          </div>
          {!approvalList.loading && attentionRows.length === 0 ? (
            <EmptyState title={t('No records need immediate attention')} description={t('The current filters have no blocking exceptions or pending approval handoffs.')} />
          ) : null}
          {attentionRows.length > 0 ? (
            <div className="timesheet-attention-list">
              {attentionRows.map((row) => {
                const issues = exceptionIssues(row)
                const firstIssue = issues[0]
                return (
                  <article className="timesheet-attention-row" key={`attention-${row.shift_id}`}>
                    <div>
                      <strong>{row.worker_name || row.worker_email || t('Worker')}</strong>
                      <span className="helper">{dateTime(row.start_time || row.scheduled_start)} · {row.job_title || row.location_name || t('Shift')}</span>
                    </div>
                    <div>
                      <span className={firstIssue?.severity === 'blocking' ? 'status-badge status-badge--warning' : 'status-badge'}>{t(firstIssue?.label || reviewStatusLabel(row))}</span>
                      <span className="helper">{t(firstIssue?.action || 'Review payable-time decision')}</span>
                    </div>
                    <div>
                      <span className="status-badge">{t(reviewStatusLabel(row))}</span>
                      <span className="helper">{t(settlementStatusLabel(row))}</span>
                    </div>
                    <button type="button" className="btn btn-ghost" onClick={() => openTimesheet(row)}>{t('Review evidence')}</button>
                  </article>
                )
              })}
            </div>
          ) : null}
        </section>
      ) : null}

      {isSettlementMode ? (
        <section className="panel workspace-panel stack">
          <div>
            <h3 style={{ margin: 0 }}>{t('Export readiness')}</h3>
            <p className="helper">{t('Payroll export only considers approved, payroll-locked payable time. Pending review, rejected, disputed, adjusted-awaiting-worker, already exported, and blocking-exception rows stay out of export.')}</p>
          </div>
          <div className="admin-checklist-grid">
            <div className="admin-checklist-item"><span>{formatNumber(settlementSummary.ready)}</span><strong>{t('Approved and ready')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(settlementSummary.blocked)}</span><strong>{t('Blocked')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(settlementSummary.alreadyExported)}</span><strong>{t('Already exported')}</strong></div>
            <div className="admin-checklist-item"><span>{formatNumber(settlementSummary.pendingReview)}</span><strong>{t('Pending review')}</strong></div>
            <div className="admin-checklist-item"><span>{minutesLabel(settlementSummary.payableMinutes)}</span><strong>{t('Ready payable time')}</strong></div>
            <div className="admin-checklist-item"><span>{settlementSummary.currencies}</span><strong>{t('Currencies')}</strong></div>
          </div>
        </section>
      ) : null}

      <section className="panel workspace-panel stack">
        <div className="admin-panel-toolbar">
          <div>
            <h3 style={{ margin: 0 }}>{t(approvalQueueTitle)}</h3>
            <p className="helper">{t(approvalQueueDescription)}</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={loadApprovals} disabled={approvalList.loading}>{t('Refresh')}</button>
        </div>
        <ErrorBanner message={approvalError} />
        <div className="admin-chip-list">
          {approvalReviewStates.map((state) => (
            <span key={state} className="admin-chip">{translatedStatus(state)}: {formatNumber(approvalRows.filter((row) => approvalStateOf(row) === state).length)}</span>
          ))}
        </div>
        {approvalList.loading && approvalRows.length === 0 ? <LoadingState label={t('Loading completed shifts...')} /> : null}
        {!approvalList.loading && approvalRows.length === 0 ? (
          <EmptyState title={t(isSettlementMode ? 'No export-ready rows matched' : 'No shifts are waiting for approval.')} description={t(isSettlementMode ? 'Approved company and payroll-locked rows will appear here when they match the export filters.' : 'Completed shifts will appear here after workers punch out or submit time. Worker challenges and disputed punches will appear in the review queue.')} />
        ) : null}
        {(isSettlementMode ? settlementRows : approvalRows).length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table timesheet-primary-table" aria-label={t(isSettlementMode ? 'Payroll export readiness rows' : 'Timesheet approval rows')}>
              <thead>
                <tr>
                  <th><SortableHeader label={industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker / date')} sortKey="worker" activeSortBy={approvalList.sortBy} sortDir={approvalList.sortDir} onSort={approvalList.setSort} /></th>
                  <th>{industryLabels.explicitIndustry ? t('{location} / {job}', { location: industryLabels.label('location'), job: industryLabels.label('job').toLowerCase() }) : t('Shift / job')}</th>
                  <th>{t('Time')}</th>
                  <th>{t('Exception')}</th>
                  <th><SortableHeader label={t('Review / export')} sortKey="approvalState" activeSortBy={approvalList.sortBy} sortDir={approvalList.sortDir} onSort={approvalList.setSort} /></th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(isSettlementMode ? settlementRows : approvalRows).map((row) => {
                  const breakMinutes = Number(row.break_minutes || 0)
                  const rowBlockers = settlementBlockers(row)
                  return (
                    <tr key={row.shift_id}>
                      <td>
                        {row.worker_name || row.worker_email || (industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker'))}
                        <br /><span className="helper">{dateTime(row.start_time || row.scheduled_start)}</span>
                        <br /><span className="helper">{row.worker_email || '-'}</span>
                      </td>
                      <td>
                        {row.job_title || row.location_name || (industryLabels.explicitIndustry ? industryLabels.label('job') : t('Shift'))}
                        <br /><span className="helper">{row.location_name || '-'}</span>
                        <br /><span className="helper">{[row.company_org_name, row.agency_org_name].filter(Boolean).join(' / ') || '-'}</span>
                      </td>
                      <td>
                        <span className="timesheet-duration-line">{t('Scheduled')}: {minutesLabel(scheduledMinutes(row))}</span>
                        <span className="timesheet-duration-line">{t('Worked evidence')}: {minutesLabel(workedMinutes(row))}</span>
                        <span className="timesheet-duration-line">{t('Payable')}: {minutesLabel(payableMinutes(row))}</span>
                        <span className="helper">{t(payableSourceLabel(row))}</span>
                        <span className="helper">{t('Breaks')}: {minutesLabel(breakMinutes)}</span>
                      </td>
                      <td>
                        <span className={hasBlockingException(row) ? 'status-badge status-badge--warning' : 'status-badge'}>{t(primaryExceptionLabel(row))}</span>
                        {exceptionIssues(row).slice(1, 3).map((issue) => (
                          <span key={issue.label} className="helper timesheet-duration-line">{t(issue.label)}</span>
                        ))}
                      </td>
                      <td>
                        <div className="admin-chip-list">
                          <span className="status-badge">{t(reviewStatusLabel(row))}</span>
                          <span className={rowBlockers.length ? 'status-badge status-badge--warning' : 'status-badge'}>{t(settlementStatusLabel(row))}</span>
                          {row.payroll_locked ? <span className="status-badge">{t('Payroll locked')}</span> : null}
                        </div>
                      </td>
                      <td>
                        <div className="inline-group">
                          <button type="button" className="btn btn-ghost" onClick={() => openTimesheet(row)}>{t(isSettlementMode ? 'Review' : 'Review evidence')}</button>
                          {canApproveRow(row) && canReviewTimesheets && !isSettlementMode ? <button type="button" className="btn btn-primary" onClick={() => openTimesheet(row)}>{t('Approve / reject')}</button> : null}
                          {canResolveRow(row) && canReviewTimesheets ? <button type="button" className="btn btn-secondary" onClick={() => openResolveTimesheet(row)}>{t('Resolve')}</button> : null}
                        </div>
                        {isSettlementMode && rowBlockers.length ? <span className="helper">{t('Blocked')}: {rowBlockers.slice(0, 2).map((blocker) => t(blocker)).join(', ')}</span> : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <PaginationControls pagination={approvalList.pagination} onPageChange={approvalList.setPage} onPageSizeChange={approvalList.setPageSize} />
          </div>
        ) : null}
      </section>

      {isSettlementMode ? (
      <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
        <div className="inline-group" style={{ alignItems: 'center' }}>
          {currentLogoUrl ? <img src={currentLogoUrl} alt="" className="admin-brand-logo admin-brand-logo--sm" /> : null}
          <div>
          <h3 style={{ marginTop: 0 }}>{t('Selected export summary')}</h3>
          <p className="helper">{industryLabels.explicitIndustry ? t('Downloads include {worker}, {location}, {job}, {assignment}, agency, company, gross duration, break duration, net payable duration, offline review, and status columns. PDF adds a printable summary and paginated detail rows.', { worker: industryLabels.label('worker').toLowerCase(), location: industryLabels.label('location').toLowerCase(), job: industryLabels.label('job').toLowerCase(), assignment: industryLabels.label('assignment').toLowerCase() }) : t('Downloads include worker, location, job, assignment, agency, company, gross duration, break duration, net payable duration, offline review, and status columns. PDF adds a printable summary and paginated detail rows.')}</p>
          </div>
        </div>
        <div className="admin-chip-list">
          {summaryChips.map((chip) => <span key={chip} className="admin-chip">{chip}</span>)}
        </div>
        {!loadingOptions && jobs.length === 0 && workerOptions.length === 0 && assignments.length === 0 ? (
          <p className="admin-disabled-callout">{industryLabels.explicitIndustry ? t('No filter options are loaded yet. You can still export the selected date range if the backend has matching {timesheet} rows.', { timesheet: industryLabels.label('timesheet').toLowerCase() }) : t('No filter options are loaded yet. You can still export the selected date range if the backend has matching timesheet rows.')}</p>
        ) : null}
      </section>
      ) : null}

      {isSettlementMode ? (
      <section className="panel workspace-panel stack">
        <div className="admin-panel-toolbar">
          <div className="inline-group" style={{ alignItems: 'center' }}>
            {currentLogoUrl ? <img src={currentLogoUrl} alt="" className="admin-brand-logo admin-brand-logo--sm" /> : null}
            <div>
            <h3 style={{ margin: 0 }}>{t('Export row preview')}</h3>
            <p className="helper">{t('Preview uses the same CSV export endpoint and current filters. Download is separate so you can check rows first.')}</p>
            </div>
          </div>
          <button type="button" className="btn btn-secondary" onClick={handlePreview} disabled={previewLoading || downloading || loadingOptions}>
            {previewLoading ? t('Loading preview...') : t('Refresh preview')}
          </button>
        </div>
        {previewLoading ? <LoadingState label={industryLabels.explicitIndustry ? t('Loading matching {timesheet} rows...', { timesheet: industryLabels.label('timesheet').toLowerCase() }) : t('Loading matching timesheet rows...')} /> : null}
        {!previewLoading && !previewLoaded ? (
          <EmptyState title={t('Preview not loaded')} description={t('Choose filters, then preview rows to see what the CSV will include before downloading.')} />
        ) : null}
        {!previewLoading && previewLoaded && previewRows.length === 0 ? (
          <EmptyState title={industryLabels.explicitIndustry ? t('No {timesheet} rows matched', { timesheet: industryLabels.label('timesheet').toLowerCase() }) : t('No timesheet rows matched')} description={industryLabels.explicitIndustry ? t('No {shifts} matched the selected date range and filters. Try widening the dates or clearing {worker}, {location}, {job}, agency, company, or {assignment} filters.', { shifts: industryLabels.label('shifts').toLowerCase(), worker: industryLabels.label('worker').toLowerCase(), location: industryLabels.label('location').toLowerCase(), job: industryLabels.label('job').toLowerCase(), assignment: industryLabels.label('assignment').toLowerCase() }) : t('No shifts matched the selected date range and filters. Try widening the dates or clearing worker, location, job, agency, company, or assignment filters.')} />
        ) : null}
        {previewRows.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</th>
                  <th>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}</th>
                  <th>{industryLabels.explicitIndustry ? t('{job} / {assignment}', { job: industryLabels.label('job'), assignment: industryLabels.label('assignment').toLowerCase() }) : t('Job / assignment')}</th>
                  <th>{t('Gross duration')}</th>
                  <th>{t('Break duration')}</th>
                  <th>{t('Net payable')}</th>
                  <th>{t('Approval')}</th>
                  <th>{t('Rate')}</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 25).map((row, index) => {
                  const assignment = assignments.find((item) => item.id === row['Job Assignment ID'])
                  const job = jobs.find((item) => item.id === row['Job ID'])
                  return (
                    <tr key={`${row['Shift ID'] || index}`}>
                      <td>{row['Worker Name'] || row['Worker Email'] || (industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker'))}<br /><span className="helper">{row['Worker Email'] || '-'}</span></td>
                      <td>{row['Location Name'] || (industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location'))}<br /><span className="helper">{row['Company Org Name'] || row['Agency Org Name'] || '-'}</span></td>
                      <td>{row['Job Name'] || job?.title || (industryLabels.explicitIndustry ? t('{job} / {assignment}', { job: industryLabels.label('job'), assignment: industryLabels.label('assignment').toLowerCase() }) : t('Job / assignment'))}<br /><span className="helper">{t(readable(row['Assignment Status']))}</span></td>
                      <td>{minutesLabel(row['Gross Minutes'])}</td>
                      <td>{minutesLabel(row['Break Minutes'])}</td>
                      <td>{minutesLabel(row['Net Payable Minutes'])}</td>
                      <td><span className="status-badge">{t(readable(row['Approval State'] || row['Status'] || row['Offline Review Status']))}</span></td>
                      <td>{canViewPayRates ? rateLabel(assignment, job) : t('Hidden by role')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {previewRows.length > 25 ? <p className="helper">{t('Showing the first 25 of {count} matching rows. The CSV download includes all matching rows.', { count: previewRows.length })}</p> : null}
          </div>
        ) : null}
      </section>
      ) : null}

      {isSettlementMode ? (
      <section className="panel workspace-panel stack">
        <div className="admin-panel-toolbar">
          <div>
            <h3 style={{ margin: 0 }}>{t('Export history')}</h3>
            <p className="helper">{t('Generated export runs record timestamp, user, filters, template label, format, and row counts for audit review.')}</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={loadExportHistory} disabled={exportHistoryList.loading}>{t('Refresh')}</button>
        </div>
        {exportHistoryList.loading && exportHistory.length === 0 ? <LoadingState label={t('Loading export history...')} /> : null}
        {!exportHistoryList.loading && exportHistory.length === 0 ? <EmptyState title={t('No exports recorded yet')} description={t('CSV, JSON, XLSX, and PDF export generations will appear here after they are downloaded or previewed.')} /> : null}
        {exportHistory.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th>{t('Generated')}</th><th>{t('Format')}</th><th>{t('Template')}</th><th>{t('Rows')}</th><th>{t('Generated by')}</th><th>{t('Filters')}</th></tr></thead>
              <tbody>{exportHistory.map((item) => (
                <tr key={item.id}>
                  <td>{dateTime(item.generated_at)}</td>
                  <td>{String(item.format || '').toUpperCase()}</td>
                  <td>{item.export_profile_label || t('Punch standard payroll')}</td>
                  <td>{item.row_count}<br /><span className="helper">{t('Marked: {count}', { count: item.marked_exported_count || 0 })}</span></td>
                  <td>{item.generated_by_email || item.generated_by_user_id || '-'}</td>
                  <td><span className="helper">{Object.entries(item.filter_summary || {}).filter(([, value]) => Boolean(value)).slice(0, 4).map(([key, value]) => `${t(readable(key))}: ${String(value)}`).join(' | ') || t('No filters')}</span></td>
                </tr>
              ))}</tbody>
            </table>
            <PaginationControls pagination={exportHistoryList.pagination} onPageChange={exportHistoryList.setPage} onPageSizeChange={exportHistoryList.setPageSize} />
          </div>
        ) : null}
      </section>
      ) : null}

      {isApprovalsMode ? (
      <section className="panel workspace-panel stack">
        <div className="admin-panel-toolbar">
          <div>
            <h3 style={{ margin: 0 }}>{t('Offline punches pending review')}</h3>
            <p className="helper">{t('Synced offline punches stay pending until a supervisor/admin approves or rejects them.')}</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={loadOfflinePunches} disabled={offlineLoading}>{t('Refresh')}</button>
        </div>
        <ErrorBanner message={offlineError} />
        {offlineLoading ? <LoadingState label={t('Loading offline punches...')} /> : null}
        {!offlineLoading && offlinePunches.length === 0 ? (
          <EmptyState title={t('No offline punches pending review')} description={t('Queued offline punches will appear here after Android syncs them.')} />
        ) : null}
        {offlinePunches.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</th>
                  <th>{t('Action')}</th>
                  <th>{t('Captured')}</th>
                  <th>{t('Received')}</th>
                  <th>{t('Sync')}</th>
                  <th>GPS</th>
                  <th>{t('Status')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {offlinePunches.map((punch) => (
                  <tr key={punch.id}>
                    <td>{punch.worker_name || punch.worker_email || (industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker'))}<br /><span className="helper">{punch.location_name || punch.location_key || '-'}</span></td>
                    <td>{t(readable(punch.action))}<br /><span className="helper">{punch.job_title || t(readable(punch.sync_status)) || '-'}</span></td>
                    <td>{dateTime(punch.device_captured_at)}</td>
                    <td>{dateTime(punch.server_received_at)}</td>
                    <td>{punch.sync_delay_seconds ?? 0}s<br /><span className="admin-chip">{t('Offline synced')}</span></td>
                    <td>{punch.accuracy_meters == null ? '-' : `${Number(punch.accuracy_meters).toFixed(1)}m`}<br /><span className={punch.fence_matched === false ? 'status-badge status-badge--warning' : 'status-badge'}>{punch.fence_matched === false ? t('Geofence review') : t('Fence matched')}</span></td>
                    <td><span className="status-badge status-badge--warning">{t(readable(punch.sync_status))}</span></td>
                    <td>
                      <div className="inline-group">
                        <button type="button" className="btn btn-ghost" onClick={() => setSelectedPunch(punch)}>{t('Details')}</button>
                        <button type="button" className="btn btn-primary" onClick={() => setApproveTarget(punch)}>{t('Approve')}</button>
                        <button type="button" className="btn btn-danger" onClick={() => setRejectTarget(punch)}>{t('Reject')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
      ) : null}

      <FormModal title={t('Offline punch details')} isOpen={Boolean(selectedPunch)} onClose={() => setSelectedPunch(null)} size="lg">
        {selectedPunch ? (
          <div className="admin-detail-grid">
            <div><strong>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</strong><span>{selectedPunch.worker_name || selectedPunch.worker_email || '-'}</span></div>
            <div><strong>{industryLabels.explicitIndustry ? industryLabels.label('assignment') : t('Assignment')}</strong><span>{selectedPunch.job_title || t(readable(selectedPunch.sync_status)) || '-'}</span></div>
            <div><strong>{t('Device captured')}</strong><span>{dateTime(selectedPunch.device_captured_at)}</span></div>
            <div><strong>{t('Server received')}</strong><span>{dateTime(selectedPunch.server_received_at)}</span></div>
            <div><strong>{t('Offline reason')}</strong><span>{t(readable(selectedPunch.offline_reason))}</span></div>
            <div><strong>{t('GPS accuracy')}</strong><span>{selectedPunch.accuracy_meters == null ? '-' : `${Number(selectedPunch.accuracy_meters).toFixed(1)}m`}</span></div>
            <div><strong>{t('Geofence')}</strong><span>{selectedPunch.fence_matched === false ? t('Needs supervisor review') : t('Matched')}</span></div>
            <div><strong>{t('Message')}</strong><span>{selectedPunch.message || '-'}</span></div>
          </div>
        ) : null}
      </FormModal>

      <FormModal
        title={t('{timesheet} review', { timesheet: industryLabels.label('timesheet') })}
        isOpen={Boolean(selectedTimesheet)}
        onClose={() => setSelectedTimesheet(null)}
        size="lg"
        footer={selectedTimesheet ? (
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setSelectedTimesheet(null)} disabled={reviewBusy}>{t('Close')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => { setHistoryTimesheet(selectedTimesheet); setSelectedTimesheet(null) }} disabled={reviewBusy}>{t('View punches and trail')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => handleAddNote(selectedTimesheet)} disabled={reviewBusy || !approvalNote.trim()}>{t('Add note')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => openAdjustTimesheet(selectedTimesheet)} disabled={reviewBusy || !canReviewTimesheets || !canAdjustRow(selectedTimesheet)}>{t('Adjust')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => openResolveTimesheet(selectedTimesheet)} disabled={reviewBusy || !canReviewTimesheets || !canResolveRow(selectedTimesheet)}>{t('Resolve dispute')}</button>
            <button type="button" className="btn btn-danger" onClick={() => setRejectTargetTimesheet(selectedTimesheet)} disabled={reviewBusy || !canReviewTimesheets || !canRejectRow(selectedTimesheet)}>{t('Reject')}</button>
            <button type="button" className="btn btn-primary" onClick={() => setApproveTargetTimesheet(selectedTimesheet)} disabled={reviewBusy || !canReviewTimesheets || !canApproveRow(selectedTimesheet)}>{t('Approve')}</button>
          </>
        ) : null}
      >
        {selectedTimesheet ? (
          <div className="stack">
            <div className="admin-detail-grid">
              <div><strong>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</strong><span>{selectedTimesheet.worker_name || selectedTimesheet.worker_email || '-'}</span></div>
              <div><strong>{t('Company')}</strong><span>{selectedTimesheet.company_org_name || '-'}</span></div>
              <div><strong>{t('Agency')}</strong><span>{selectedTimesheet.agency_org_name || '-'}</span></div>
              <div><strong>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}</strong><span>{selectedTimesheet.location_name || '-'}</span></div>
              <div><strong>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job / order')}</strong><span>{selectedTimesheet.job_title || '-'}</span></div>
              <div><strong>{industryLabels.explicitIndustry ? industryLabels.label('assignment') : t('Assignment')}</strong><span>{selectedTimesheet.assignment_status ? t(readable(selectedTimesheet.assignment_status)) : '-'}</span></div>
              <div><strong>{t('Scheduled')}</strong><span>{dateTime(selectedTimesheet.scheduled_start)} {t('to')} {dateTime(selectedTimesheet.scheduled_end)}</span></div>
              <div><strong>{t('Actual')}</strong><span>{dateTime(selectedTimesheet.start_time)} {t('to')} {dateTime(selectedTimesheet.end_time)}</span></div>
              <div><strong>{t('Breaks')}</strong><span>{dateTime(selectedTimesheet.first_break_start)} {t('to')} {dateTime(selectedTimesheet.last_break_end)} ({minutesLabel(selectedTimesheet.break_minutes)})</span></div>
              <div><strong>{t('Gross duration')}</strong><span>{minutesLabel(grossMinutes(selectedTimesheet))}</span></div>
              <div><strong>{t('Worked duration')}</strong><span>{minutesLabel(workedMinutes(selectedTimesheet))}</span></div>
              <div><strong>{t('Payable duration')}</strong><span>{minutesLabel(payableMinutes(selectedTimesheet))} ({t(payableSourceLabel(selectedTimesheet))})</span></div>
              <div><strong>{t('Pay rate')}</strong><span>{canViewPayRates ? (selectedTimesheet.pay_rate == null ? '-' : `${Number(selectedTimesheet.pay_rate).toFixed(2)} ${selectedTimesheet.currency_code || selectedTimesheet.currencyCode || ''}/hr`) : t('Hidden by role')}</span></div>
              <div><strong>{t('Punch source / offline review')}</strong><span>{t('See offline punch queue and sync diagnostics when present')}</span></div>
              <div><strong>{t('Geofence / offsite diagnostics')}</strong><span>{t('See punch/offline review and location audit when present')}</span></div>
              <div><strong>{t('Notes / exceptions')}</strong><span>{selectedTimesheet.reason || '-'}</span></div>
              <div><strong>{t('Approval status')}</strong><span>{t(readable(selectedTimesheet.approval_state))}</span></div>
              <div><strong>{industryLabels.explicitIndustry ? t('{worker} dispute', { worker: industryLabels.label('worker') }) : t('Worker dispute')}</strong><span>{selectedTimesheet.worker_dispute_message || '-'}</span></div>
              <div><strong>{industryLabels.explicitIndustry ? t('{worker} disputed at', { worker: industryLabels.label('worker') }) : t('Worker disputed at')}</strong><span>{dateTime(selectedTimesheet.worker_disputed_at)}</span></div>
              <div><strong>{t('Admin decision')}</strong><span>{t(readable(selectedTimesheet.admin_decision))}</span></div>
              <div><strong>{t('Admin adjustment')}</strong><span>{selectedTimesheet.admin_adjustment_reason || '-'}</span></div>
              <div><strong>{t('Adjusted time')}</strong><span>{dateTime(selectedTimesheet.admin_adjusted_start_time)} {t('to')} {dateTime(selectedTimesheet.admin_adjusted_end_time)}</span></div>
              <div><strong>{t('Adjusted break / total')}</strong><span>{minutesLabel(selectedTimesheet.admin_adjusted_break_minutes)} / {selectedTimesheet.admin_adjusted_total_hours == null ? '-' : `${Number(selectedTimesheet.admin_adjusted_total_hours).toFixed(2)} hr`}</span></div>
              <div><strong>{t('Final time')}</strong><span>{dateTime(selectedTimesheet.final_start_time)} {t('to')} {dateTime(selectedTimesheet.final_end_time)}</span></div>
              <div><strong>{t('Final break / payable')}</strong><span>{minutesLabel(selectedTimesheet.final_break_minutes)} / {selectedTimesheet.final_total_hours == null ? '-' : `${Number(selectedTimesheet.final_total_hours).toFixed(2)} hr`}</span></div>
              <div><strong>{t('Export status')}</strong><span>{t(rowExportStatus(selectedTimesheet))}</span></div>
              <div><strong>{t('Readiness')}</strong><span>{t(readable(readinessStatusOf(selectedTimesheet)))}{settlementBlockers(selectedTimesheet).length ? ` - ${settlementBlockers(selectedTimesheet).slice(0, 2).map((blocker) => t(blocker)).join(', ')}` : ''}</span></div>
              <div><strong>{t('Exception state')}</strong><span>{exceptionIssues(selectedTimesheet).map((issue) => t(issue.label)).join(', ') || t('No blocking exception')}</span></div>
              <div><strong>{t('Dispute resolution')}</strong><span>{selectedTimesheet.dispute_resolution_note || '-'}</span></div>
              <div><strong>{industryLabels.explicitIndustry ? t('{payroll} lock', { payroll: industryLabels.label('payroll') }) : t('Payroll lock')}</strong><span>{selectedTimesheet.payroll_locked ? t('{payroll} locked', { payroll: industryLabels.label('payroll') }) : t('Not {payroll} locked', { payroll: industryLabels.label('payroll').toLowerCase() })}</span></div>
            </div>
            <label>{t('Reviewer note')}<textarea className="input" value={approvalNote} onChange={(event) => setApprovalNote(event.target.value)} placeholder={t('Add approval, rejection, or audit context')} /></label>
            <div>
              <h4>{t('Audit trail')}</h4>
              {approvalHistory.length === 0 ? <p className="helper">{t('No approval history recorded yet.')}</p> : null}
              {approvalHistory.map((item) => (
                <p key={item.id} className="helper">{dateTime(item.created_at)} - {t(readable(item.action))} {t('by')} {item.actor_email || item.actor_user_id || t('system')} ({t(readable(item.from_state || ''))} {t('to')} {t(readable(item.to_state || ''))}) {item.reason ? `- ${item.reason}` : ''}</p>
              ))}
            </div>
          </div>
        ) : null}
      </FormModal>

      <FormModal
        title={t('Punches and GPS trail')}
        description={historyTimesheet ? `${historyTimesheet.worker_name || historyTimesheet.worker_email || t('Worker')} · ${historyTimesheet.location_name || t('Shift')}` : undefined}
        isOpen={Boolean(historyTimesheet)}
        onClose={() => setHistoryTimesheet(null)}
        size="xl"
      >
        {historyTimesheet ? (
          <ShiftHistoryViewer
            locationId={historyTimesheet.location_id || undefined}
            assignmentId={historyTimesheet.job_assignment_id || undefined}
            initialShiftGroupId={historyTimesheet.job_assignment_id ? `a:${historyTimesheet.job_assignment_id}` : `w:${historyTimesheet.shift_id}`}
          />
        ) : null}
      </FormModal>

      <ConfirmDialog
        title={t('Approve timesheet')}
        message={t('Approve this reviewed payable-time record for payroll export eligibility. Attendance evidence remains auditable, and unresolved blocking exceptions should be corrected before approval.')}
        isOpen={Boolean(approveTargetTimesheet)}
        disabled={reviewBusy}
        onCancel={() => setApproveTargetTimesheet(null)}
        onConfirm={() => approveTargetTimesheet ? handleApproveTimesheet(approveTargetTimesheet) : undefined}
        confirmLabel={t('Approve')}
      />

      <ConfirmDialog
        title={t('Reject timesheet')}
        message={t('Rejected timesheets remain auditable and are excluded from approved payable exports.')}
        isOpen={Boolean(rejectTargetTimesheet)}
        disabled={reviewBusy || !approvalNote.trim()}
        onCancel={() => setRejectTargetTimesheet(null)}
        onConfirm={handleRejectTimesheet}
        confirmLabel={t('Reject')}
      />

      <FormModal
        title={t('Adjust timesheet')}
        isOpen={Boolean(adjustTargetTimesheet)}
        onClose={() => setAdjustTargetTimesheet(null)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setAdjustTargetTimesheet(null)} disabled={reviewBusy}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={handleAdjustTimesheet} disabled={reviewBusy || !adjustForm.adjustedStartTime || !adjustForm.adjustedEndTime || !adjustForm.reason.trim()}>{t('Save adjustment')}</button>
          </>
        )}
      >
        <div className="admin-form-grid">
          <label>{t('Adjusted start time')}<input className="input" type="datetime-local" value={adjustForm.adjustedStartTime ? adjustForm.adjustedStartTime.slice(0, 16) : ''} onChange={(event) => setAdjustForm((current) => ({ ...current, adjustedStartTime: event.target.value }))} /></label>
          <label>{t('Adjusted end time')}<input className="input" type="datetime-local" value={adjustForm.adjustedEndTime ? adjustForm.adjustedEndTime.slice(0, 16) : ''} onChange={(event) => setAdjustForm((current) => ({ ...current, adjustedEndTime: event.target.value }))} /></label>
          <label>{t('Adjusted break minutes')}<input className="input" type="number" min="0" value={adjustForm.adjustedBreakMinutes} onChange={(event) => setAdjustForm((current) => ({ ...current, adjustedBreakMinutes: Number(event.target.value || 0) }))} /></label>
          <label>{t('Final payable hours')}<input className="input" type="number" min="0" step="0.01" value={adjustForm.adjustedTotalHours} onChange={(event) => setAdjustForm((current) => ({ ...current, adjustedTotalHours: Number(event.target.value || 0) }))} /></label>
        </div>
        <label>{t('Adjustment reason')}<textarea className="input" value={adjustForm.reason} onChange={(event) => setAdjustForm((current) => ({ ...current, reason: event.target.value }))} /></label>
        <p className="helper">{t('Admin adjustments are not export-ready until the worker accepts the adjustment or a dispute is resolved into final payroll values.')}</p>
      </FormModal>

      <FormModal
        title={t('Resolve timesheet dispute')}
        isOpen={Boolean(resolveTargetTimesheet)}
        onClose={() => setResolveTargetTimesheet(null)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setResolveTargetTimesheet(null)} disabled={reviewBusy}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={handleResolveTimesheet} disabled={reviewBusy || !resolveForm.note?.trim() || (resolveForm.resolution === 'manual_final_values' && (!resolveForm.finalStartTime || !resolveForm.finalEndTime))}>{t('Resolve dispute')}</button>
          </>
        )}
      >
        <label>{t('Resolution outcome')}<select className="input" value={resolveForm.resolution} onChange={(event) => setResolveForm((current) => ({ ...current, resolution: event.target.value as AdminTimesheetResolvePayload['resolution'] }))}><option value="accept_worker_version">{t('Accept worker version')}</option><option value="keep_admin_version">{t('Keep admin version')}</option><option value="manual_final_values">{t('Manual final values')}</option></select></label>
        {resolveForm.resolution === 'manual_final_values' ? (
          <div className="admin-form-grid">
            <label>{t('Final start time')}<input className="input" type="datetime-local" value={resolveForm.finalStartTime ? resolveForm.finalStartTime.slice(0, 16) : ''} onChange={(event) => setResolveForm((current) => ({ ...current, finalStartTime: event.target.value }))} /></label>
            <label>{t('Final end time')}<input className="input" type="datetime-local" value={resolveForm.finalEndTime ? resolveForm.finalEndTime.slice(0, 16) : ''} onChange={(event) => setResolveForm((current) => ({ ...current, finalEndTime: event.target.value }))} /></label>
            <label>{t('Final break minutes')}<input className="input" type="number" min="0" value={resolveForm.finalBreakMinutes ?? 0} onChange={(event) => setResolveForm((current) => ({ ...current, finalBreakMinutes: Number(event.target.value || 0) }))} /></label>
            <label>{t('Final payable hours')}<input className="input" type="number" min="0" step="0.01" value={resolveForm.finalTotalHours ?? 0} onChange={(event) => setResolveForm((current) => ({ ...current, finalTotalHours: Number(event.target.value || 0) }))} /></label>
          </div>
        ) : null}
        <label>{t('Resolution note')}<textarea className="input" value={resolveForm.note || ''} onChange={(event) => setResolveForm((current) => ({ ...current, note: event.target.value }))} /></label>
        <p className="helper">{t('Resolved disputes write final values, payroll-lock the row, and keep the approval history intact.')}</p>
      </FormModal>

      <ConfirmDialog
        title={t('Approve offline punch')}
        message={t('Approved offline punches are applied to the timesheet and become eligible for payroll export.')}
        isOpen={Boolean(approveTarget)}
        disabled={reviewBusy}
        onCancel={() => setApproveTarget(null)}
        onConfirm={confirmApprove}
        confirmLabel={t('Approve')}
      />

      <FormModal
        title={t('Reject offline punch')}
        isOpen={Boolean(rejectTarget)}
        onClose={() => setRejectTarget(null)}
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setRejectTarget(null)} disabled={reviewBusy}>{t('Cancel')}</button>
            <button type="button" className="btn btn-danger" onClick={confirmReject} disabled={reviewBusy || !rejectionReason.trim()}>{t('Reject')}</button>
          </>
        )}
      >
        <label>{t('Reason')}<input className="input" value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} /></label>
        <label>{t('Notes')}<textarea className="input" value={reviewNotes} onChange={(event) => setReviewNotes(event.target.value)} /></label>
      </FormModal>
    </div>
  )
}
